import { checkSession } from "@features/auth";
import { useAsyncMutation } from "@hooks/useAsyncMutation";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { preloadQuery } from "@relay";
import { Badge } from "@shadcn/ui/badge";
import { Button } from "@shadcn/ui/button";
import { Card, CardContent } from "@shadcn/ui/card";
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from "@shadcn/ui/drawer";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { createFileRoute, redirect, useNavigate, useRouter } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { formatLockCouponDuration } from "@utils/lock-coupon";
import { stripePromise } from "@utils/stripe";
import { CreditCard, Gift, LoaderCircle, TicketPercent } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { usePreloadedQuery, useRelayEnvironment } from "react-relay";
import { fetchQuery, graphql } from "relay-runtime";
import { toast } from "sonner";
import { z } from "zod";
import { AppHeader } from "~/components/layout/AppHeader";
import { PageContainer } from "~/components/layout/PageContainer";
import { ServiceBreadcrumb } from "~/components/lock/ServiceBreadcrumb";
import type { lockCouponsHubQuery } from "./__generated__/lockCouponsHubQuery.graphql";
import type { lockCouponsPurchaseMutation } from "./__generated__/lockCouponsPurchaseMutation.graphql";
import type { lockCouponsPurchaseStatusQuery } from "./__generated__/lockCouponsPurchaseStatusQuery.graphql";
import type { lockCouponsQuery, lockCouponsQuery$data } from "./__generated__/lockCouponsQuery.graphql";

const hubQuery = graphql`
	query lockCouponsHubQuery($hubName: String!) {
		hubMeta(by: { name: $hubName }) {
			id
			hub {
				id
			}
		}
	}
`;

const query = graphql`
	query lockCouponsQuery($hubName: String!, $hubId: UUID!) @relay_test_operation {
		hubMeta(by: { name: $hubName }) @required(action: THROW) {
			id
			name
			hub @required(action: THROW) {
				id
			}
		}
		b2CCouponBalance(hubId: $hubId) {
			available
			reserved
			consumed
		}
		b2CCouponOfferAvailability(hubId: $hubId) {
			purchasable
			remainingForUser
			remainingInHub
			offer {
				id
				title
				description
				coupons
				freeMinutes
				priceCents
			}
		}
		b2CCoupons(pagination: { page: 0, pageSize: 50 }) {
			totalCount
			nodes {
				id
				status
				freeMinutes
				hubId
				expiresAt
				note
				purchaseId
				createdAt
			}
		}
	}
`;

const purchaseMutation = graphql`
	mutation lockCouponsPurchaseMutation($offerId: UUID!, $hubId: UUID!) {
		b2CCouponPurchaseCreate(input: { offerId: $offerId, hubId: $hubId }) {
			clientSecret
			intentKind
			purchase {
				id
				status
				coupons
			}
		}
	}
`;

const purchaseStatusQuery = graphql`
	query lockCouponsPurchaseStatusQuery($offerId: UUID, $hubId: UUID!) {
		b2CCouponPurchases(filter: { offerId: $offerId, hubId: $hubId }, pagination: { page: 0, pageSize: 50 }) {
			nodes {
				id
				offerId
				status
				coupons
			}
		}
	}
`;

export const Route = createFileRoute("/$hubMetaName/lock/coupons")({
	beforeLoad: () => {
		document.title = "bacehub | Lock coupons";
	},
	component: RouteComponent,
	validateSearch: zodValidator(z.object({ purchaseId: z.string().uuid().optional(), offerId: z.string().uuid().optional() })),
	loader: async (ctx) => {
		if (!(await checkSession())) {
			throw redirect({
				to: "/auth",
				search: { redirectToPath: `/${ctx.params.hubMetaName}/lock/coupons${ctx.location.searchStr}` },
			});
		}
		const hubData = await fetchQuery<lockCouponsHubQuery>(ctx.context.relayEnvironment, hubQuery, {
			hubName: ctx.params.hubMetaName,
		}).toPromise();
		const hubId = hubData?.hubMeta?.hub?.id;
		if (!hubId) throw new Error("Hub not found");
		return preloadQuery<lockCouponsQuery>(
			ctx.context.relayEnvironment,
			query,
			{ hubName: ctx.params.hubMetaName, hubId },
			{ fetchPolicy: "network-only" }
		);
	},
});

type OfferAvailability = lockCouponsQuery$data["b2CCouponOfferAvailability"][number];
type Coupon = lockCouponsQuery$data["b2CCoupons"]["nodes"][number];
type Checkout = {
	clientSecret: string;
	intentKind: "PAYMENT" | "SETUP";
	purchaseId: string;
	offerId: string;
	title: string;
	priceCents: number;
};

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 60_000;

function RouteComponent() {
	const i18n = useI18nContext().LL["new-ui"];
	const { locale } = useI18nContext();
	const navigate = useNavigate();
	const router = useRouter();
	const environment = useRelayEnvironment();
	const { purchaseId: returnedPurchaseId, offerId: returnedOfferId } = Route.useSearch();
	const { hubMeta, b2CCouponBalance, b2CCouponOfferAvailability, b2CCoupons } = usePreloadedQuery<lockCouponsQuery>(
		query,
		Route.useLoaderData()
	);
	const [purchase, isPurchasing] = useAsyncMutation<lockCouponsPurchaseMutation>(purchaseMutation);
	const [pendingOfferId, setPendingOfferId] = useState<string | null>(null);
	const [processingOfferId, setProcessingOfferId] = useState<string | null>(null);
	const [checkout, setCheckout] = useState<Checkout | null>(null);
	const formatPrice = (cents: number) => new Intl.NumberFormat(locale, { style: "currency", currency: "EUR" }).format(cents / 100);

	const purchaseCompleted = async (coupons: number) => {
		setCheckout(null);
		toast.success(i18n.LOCK_COUPON_PURCHASE_SUCCESS({ count: coupons }));
		await router.invalidate();
	};

	const waitForPurchase = async (purchaseId: string, offerId?: string) => {
		setProcessingOfferId(offerId ?? "returned");
		const deadline = Date.now() + POLL_TIMEOUT_MS;
		try {
			while (Date.now() < deadline) {
				const data = await fetchQuery<lockCouponsPurchaseStatusQuery>(
					environment,
					purchaseStatusQuery,
					{ offerId: offerId ?? null, hubId: hubMeta.hub.id },
					{ fetchPolicy: "network-only" }
				).toPromise();
				const current = data?.b2CCouponPurchases.nodes.find((node) => node.id === purchaseId);
				if (current?.status === "COMPLETED") {
					await purchaseCompleted(current.coupons);
					return;
				}
				if (current?.status === "CANCELED") {
					toast.error(i18n.LOCK_COUPON_PAYMENT_CANCELED());
					return;
				}
				await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
			}
			toast.info(i18n.LOCK_COUPON_PAYMENT_DELAYED());
		} catch (error) {
			console.error(error);
			toast.info(i18n.LOCK_COUPON_PAYMENT_DELAYED());
		} finally {
			setProcessingOfferId(null);
		}
	};

	useEffect(() => {
		if (!returnedPurchaseId) return;
		void navigate({
			to: "/$hubMetaName/lock/coupons",
			params: { hubMetaName: hubMeta.name },
			search: {},
			replace: true,
		});
		void waitForPurchase(returnedPurchaseId, returnedOfferId);
	}, [returnedPurchaseId, returnedOfferId]);

	const buy = async (availability: OfferAvailability) => {
		if (pendingOfferId || processingOfferId || !availability.purchasable) return;
		const { offer } = availability;
		setPendingOfferId(offer.id);
		try {
			const { b2CCouponPurchaseCreate: result } = await purchase({ variables: { offerId: offer.id, hubId: hubMeta.hub.id } });
			if (result.purchase.status === "COMPLETED") {
				await purchaseCompleted(result.purchase.coupons);
			} else if (result.clientSecret) {
				setCheckout({
					clientSecret: result.clientSecret,
					intentKind: result.intentKind === "SETUP" ? "SETUP" : "PAYMENT",
					purchaseId: result.purchase.id,
					offerId: offer.id,
					title: offer.title,
					priceCents: offer.priceCents,
				});
			} else {
				await waitForPurchase(result.purchase.id, offer.id);
			}
		} catch (error) {
			toast.error(i18n.LOCK_COUPON_PURCHASE_FAILED(), { description: error instanceof Error ? error.message : undefined });
		} finally {
			setPendingOfferId(null);
		}
	};

	const busy = isPurchasing || !!pendingOfferId || !!processingOfferId;

	return (
		<PageContainer className="min-h-dvh bg-slate-50">
			<AppHeader service="LOCK_B2C" />
			<main className="mx-auto flex w-full max-w-[390px] flex-col gap-6 px-6 pb-8 pt-6">
				<div className="flex flex-col gap-2">
					<ServiceBreadcrumb hubMetaName={hubMeta.name} current={i18n.LOCK_COUPON_TITLE()} />
					<h1 className="text-2xl font-breit font-medium leading-8">
						<T>{i18n.LOCK_COUPON_TITLE()}</T>
					</h1>
					<p className="text-sm leading-6 text-muted-foreground">
						<T>{i18n.LOCK_COUPON_SUBTITLE()}</T>
					</p>
				</div>

				<Card className="rounded-[14px] border-neutral-200 bg-white py-0 shadow-sm">
					<CardContent className="flex items-center gap-4 p-5">
						<span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
							<TicketPercent className="size-6" />
						</span>
						<div className="min-w-0 flex-1">
							<p className="text-sm text-muted-foreground">
								<T>{i18n.LOCK_COUPON_AVAILABLE()}</T>
							</p>
							<p className="text-3xl font-medium tabular-nums">{b2CCouponBalance.available}</p>
							<p className="text-xs text-muted-foreground">
								<T>
									{i18n.LOCK_COUPON_BALANCE_DETAIL({
										reserved: b2CCouponBalance.reserved,
										consumed: b2CCouponBalance.consumed,
									})}
								</T>
							</p>
						</div>
					</CardContent>
				</Card>

				{processingOfferId ? (
					<div role="status" className="flex items-center gap-2 rounded-lg border bg-white px-4 py-3 text-sm">
						<LoaderCircle className="size-4 animate-spin" />
						<T>{i18n.LOCK_COUPON_PROCESSING()}</T>
					</div>
				) : null}

				<section className="flex flex-col gap-4" aria-labelledby="coupon-offers-title">
					<div>
						<h2 id="coupon-offers-title" className="text-lg font-medium">
							<T>{i18n.LOCK_COUPON_OFFERS_TITLE()}</T>
						</h2>
						<p className="text-sm text-muted-foreground">
							<T>{i18n.LOCK_COUPON_OFFERS_SUBTITLE()}</T>
						</p>
					</div>
					{b2CCouponOfferAvailability.length === 0 ? (
						<p className="rounded-lg border border-dashed bg-white p-6 text-center text-sm text-muted-foreground">
							<T>{i18n.LOCK_COUPON_OFFERS_EMPTY()}</T>
						</p>
					) : (
						b2CCouponOfferAvailability
							.toSorted((a, b) => Number(!a.purchasable) - Number(!b.purchasable))
							.map((availability) => (
								<OfferCard
									key={availability.offer.id}
									availability={availability}
									disabled={busy}
									loading={pendingOfferId === availability.offer.id || processingOfferId === availability.offer.id}
									price={formatPrice(availability.offer.priceCents)}
									onBuy={() => void buy(availability)}
								/>
							))
					)}
				</section>

				<section className="flex flex-col gap-3" aria-labelledby="my-coupons-title">
					<h2 id="my-coupons-title" className="text-lg font-medium">
						<T>{i18n.LOCK_COUPON_MY_TITLE()}</T>
					</h2>
					{b2CCoupons.nodes.length === 0 ? (
						<p className="rounded-lg border border-dashed bg-white p-6 text-center text-sm text-muted-foreground">
							<T>{i18n.LOCK_COUPON_MY_EMPTY()}</T>
						</p>
					) : (
						<ul className="flex flex-col gap-3">
							{b2CCoupons.nodes.map((coupon) => (
								<CouponCard key={coupon.id} coupon={coupon} currentHubId={hubMeta.hub.id} />
							))}
						</ul>
					)}
				</section>
			</main>

			<Drawer open={!!checkout} onOpenChange={(open) => !open && setCheckout(null)}>
				<DrawerContent>
					{checkout ? (
						<div className="mx-auto flex min-h-0 w-full max-w-[430px] flex-col gap-4 overflow-y-auto px-6 pb-8">
							<DrawerHeader className="px-0">
								<DrawerTitle>{checkout.title}</DrawerTitle>
								<DrawerDescription>
									{checkout.intentKind === "SETUP"
										? i18n.LOCK_COUPON_SETUP_DESCRIPTION()
										: i18n.LOCK_COUPON_PAYMENT_DESCRIPTION({ price: formatPrice(checkout.priceCents) })}
								</DrawerDescription>
							</DrawerHeader>
							<Elements stripe={stripePromise} options={{ clientSecret: checkout.clientSecret, locale }}>
								<CouponCheckoutForm
									checkout={checkout}
									hubMetaName={hubMeta.name}
									onConfirmed={() => {
										const current = checkout;
										setCheckout(null);
										void waitForPurchase(current.purchaseId, current.offerId);
									}}
								/>
							</Elements>
						</div>
					) : null}
				</DrawerContent>
			</Drawer>
		</PageContainer>
	);
}

function OfferCard({
	availability,
	disabled,
	loading,
	price,
	onBuy,
}: {
	availability: OfferAvailability;
	disabled: boolean;
	loading: boolean;
	price: string;
	onBuy: () => void;
}) {
	const i18n = useI18nContext().LL["new-ui"];
	const { offer } = availability;
	const remaining = availability.remainingForUser ?? availability.remainingInHub;
	return (
		<Card className="rounded-[14px] border-neutral-200 bg-white py-0 shadow-sm">
			<CardContent className="flex flex-col gap-4 p-5">
				<div className="flex items-start justify-between gap-3">
					<div className="flex min-w-0 flex-col gap-1">
						<h3 className="break-words text-lg font-medium">{offer.title}</h3>
						{offer.description ? <p className="text-sm leading-6 text-muted-foreground">{offer.description}</p> : null}
					</div>
					<Badge variant={offer.priceCents === 0 ? "secondary" : "outline"} className="shrink-0">
						{offer.priceCents === 0 ? i18n.LOCK_COUPON_FREE() : price}
					</Badge>
				</div>
				<div className="flex items-center gap-3 rounded-lg bg-emerald-50 px-4 py-3 text-emerald-950">
					<Gift className="size-5 shrink-0" />
					<p className="text-sm font-medium">
						<T>
							{i18n.LOCK_COUPON_OFFER_VALUE({
								count: offer.coupons,
								duration: formatLockCouponDuration(offer.freeMinutes),
							})}
						</T>
					</p>
				</div>
				{remaining != null && remaining > 0 ? (
					<p className="text-xs text-muted-foreground">
						<T>{i18n.LOCK_COUPON_REMAINING({ count: remaining })}</T>
					</p>
				) : null}
				<Button className="w-full" disabled={!availability.purchasable || disabled || loading} onClick={onBuy}>
					{loading ? <LoaderCircle className="size-4 animate-spin" /> : null}
					{availability.purchasable
						? offer.priceCents === 0
							? i18n.LOCK_COUPON_REDEEM()
							: i18n.LOCK_COUPON_BUY({ price })
						: i18n.LOCK_COUPON_LIMIT_REACHED()}
				</Button>
			</CardContent>
		</Card>
	);
}

function CouponCard({ coupon, currentHubId }: { coupon: Coupon; currentHubId: string }) {
	const i18n = useI18nContext().LL["new-ui"];
	const { locale } = useI18nContext();
	const statusLabels: Record<string, string> = {
		AVAILABLE: i18n.LOCK_COUPON_STATUS_AVAILABLE(),
		RESERVED: i18n.LOCK_COUPON_STATUS_RESERVED(),
		CONSUMED: i18n.LOCK_COUPON_STATUS_CONSUMED(),
		REVOKED: i18n.LOCK_COUPON_STATUS_REVOKED(),
	};
	return (
		<li className="flex flex-col gap-2 rounded-[12px] border bg-white p-4">
			<div className="flex items-center justify-between gap-3">
				<p className="font-medium">{i18n.LOCK_COUPON_FREE_TIME({ duration: formatLockCouponDuration(coupon.freeMinutes) })}</p>
				<Badge variant={coupon.status === "AVAILABLE" ? "default" : coupon.status === "REVOKED" ? "destructive" : "secondary"}>
					{statusLabels[coupon.status] ?? coupon.status}
				</Badge>
			</div>
			<div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
				<span>
					{coupon.hubId
						? coupon.hubId === currentHubId
							? i18n.LOCK_COUPON_THIS_HUB()
							: i18n.LOCK_COUPON_SPECIFIC_HUB()
						: i18n.LOCK_COUPON_ALL_HUBS()}
				</span>
				{coupon.expiresAt ? <span>{i18n.LOCK_COUPON_EXPIRES({ date: new Date(coupon.expiresAt).toLocaleDateString(locale) })}</span> : null}
			</div>
			{coupon.note ? <p className="text-sm text-muted-foreground">{coupon.note}</p> : null}
		</li>
	);
}

function CouponCheckoutForm({ checkout, hubMetaName, onConfirmed }: { checkout: Checkout; hubMetaName: string; onConfirmed: () => void }) {
	const i18n = useI18nContext().LL["new-ui"];
	const stripe = useStripe();
	const elements = useElements();
	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleSubmit = async (event: FormEvent) => {
		event.preventDefault();
		if (!stripe || !elements || isSubmitting) return;
		setIsSubmitting(true);
		try {
			const returnUrl = new URL(`/${hubMetaName}/lock/coupons`, window.location.origin);
			returnUrl.searchParams.set("purchaseId", checkout.purchaseId);
			returnUrl.searchParams.set("offerId", checkout.offerId);
			const confirmParams = { return_url: returnUrl.toString() };
			const result =
				checkout.intentKind === "SETUP"
					? await stripe.confirmSetup({ elements, confirmParams, redirect: "if_required" })
					: await stripe.confirmPayment({ elements, confirmParams, redirect: "if_required" });
			if (result.error) {
				toast.error(result.error.message ?? i18n.LOCK_COUPON_PAYMENT_FAILED());
				return;
			}
			onConfirmed();
		} catch (error) {
			console.error(error);
			toast.error(i18n.LOCK_COUPON_PAYMENT_FAILED());
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<form className="flex flex-col gap-5" onSubmit={(event) => void handleSubmit(event)}>
			<PaymentElement />
			<Button type="submit" size="lg" className="w-full" disabled={!stripe || !elements || isSubmitting}>
				{isSubmitting ? <LoaderCircle className="size-4 animate-spin" /> : <CreditCard className="size-4" />}
				{checkout.intentKind === "SETUP" ? i18n.LOCK_COUPON_CONFIRM_SETUP() : i18n.LOCK_COUPON_CONFIRM_PAYMENT()}
			</Button>
		</form>
	);
}
